import { create } from 'zustand';
import { useMessagingStore } from './messagingStore';

// ===== READ-STATE (Strategy A) helpers =====
// Per-user localStorage key so read-state survives logout/login but does not bleed across users.
const READ_STATE_PREFIX = 'eh_matrix_read_v1:';

const getReadStateKey = (mxid) => `${READ_STATE_PREFIX}${String(mxid || '').trim()}`;

const safeParse = (raw, fallback) => {
  try { return JSON.parse(raw); } catch { return fallback; }
};

const loadReadStateMap = (mxid) => {
  try {
    const key = getReadStateKey(mxid);
    const raw = localStorage.getItem(key);
    const parsed = safeParse(raw || '{}', {});
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
};

const saveReadStateMap = (mxid, map) => {
  try {
    const key = getReadStateKey(mxid);
    localStorage.setItem(key, JSON.stringify(map || {}));
  } catch {}
};

export const useMatrixStore = create((set, get) => ({

  // ===== MATRIX CONNECTION STATE =====
  connectionState: 'disconnected', // 'disconnected' | 'syncing' | 'connected' | 'reconnecting' | 'error'
  
  setConnectionState: (state) => {
    set({ connectionState: state });
  },
  // ===== PAGINATION STATE =====
  paginationState: {}, // { [roomId]: { hasMore: boolean, isLoading: boolean } }

  setPaginationLoading: (roomId, isLoading) => {
    set((state) => ({
      paginationState: {
        ...state.paginationState,
        [roomId]: { ...(state.paginationState[roomId] || { hasMore: true }), isLoading }
      }
    }));
  },

  setPaginationHasMore: (roomId, hasMore) => {
    set((state) => ({
      paginationState: {
        ...state.paginationState,
        [roomId]: { ...(state.paginationState[roomId] || { isLoading: false }), hasMore }
      }
    }));
  },

  // ===== MATRIX ROOMS DATA =====
  rooms: {}, // { [roomId]: roomData }
  selectedRoomId: null,
  // Rooms management
  upsertRoom: (roomData) => {
    set((state) => ({
      rooms: {
        ...state.rooms,
        [roomData.roomId]: {
          ...state.rooms[roomData.roomId],
          ...roomData,
          lastUpdated: Date.now()
        }
      }
    }));
  },

  removeRoom: (roomId) => {
    set((state) => {
      const { [roomId]: removed, ...remainingRooms } = state.rooms;
      const { [roomId]: removedMessages, ...remainingMessages } = state.messages;
      const { [roomId]: removedUnread, ...remainingUnread } = state.unreadCounts;
      const { [roomId]: removedTyping, ...remainingTyping } = state.typingUsers;
      
      return {
        rooms: remainingRooms,
        messages: remainingMessages,
        unreadCounts: remainingUnread,
        typingUsers: remainingTyping
      };
    });
  },

  // ===== TYPING INDICATORS =====
  typingUsers: {}, // { [roomId]: userId[] }

  setTypingUser: (roomId, userId, isTyping) => {
    set((state) => {
      const currentTyping = state.typingUsers[roomId] || [];
      let newTyping;
      
      if (isTyping) {
        // Add user to typing list if not already there
        newTyping = currentTyping.includes(userId) 
          ? currentTyping 
          : [...currentTyping, userId];
      } else {
        // Remove user from typing list
        newTyping = currentTyping.filter(id => id !== userId);
      }
      
      return {
        typingUsers: {
          ...state.typingUsers,
          [roomId]: newTyping
        }
      };
    });
  },

  clearAllTyping: (roomId) => {
    set((state) => ({
      typingUsers: {
        ...state.typingUsers,
        [roomId]: []
      }
    }));
  },

  // ===== REMOTE READ RECEIPTS (other users' read-up-to markers) =====
  remoteReadReceipts: {}, // { [roomId]: { [userId]: { eventId, ts } } }

  setRemoteReadReceipt: (roomId, userId, eventId, ts) => {
    set((state) => {
      const prev = state.remoteReadReceipts[roomId]?.[userId];
      // Only update if newer (or first receipt for this user/room)
      if (prev && prev.ts && ts && prev.ts >= ts) return state;
      return {
        remoteReadReceipts: {
          ...state.remoteReadReceipts,
          [roomId]: {
            ...(state.remoteReadReceipts[roomId] || {}),
            [userId]: { eventId, ts: ts || Date.now() }
          }
        }
      };
    });
  },

  // ===== EMOJI REACTIONS =====
  // { [roomId]: { [targetEventId]: { [emoji]: [{ userId, reactionEventId }] } } }
  reactions: {},

  addReaction: (roomId, targetEventId, emoji, userId, reactionEventId) => {
    set((state) => {
      const roomReactions = state.reactions[roomId] || {};
      const msgReactions = roomReactions[targetEventId] || {};
      const emojiList = msgReactions[emoji] || [];

      // Deduplicate: one reaction per user per emoji
      if (emojiList.some((r) => r.userId === userId && r.reactionEventId === reactionEventId)) {
        return state;
      }
      // Remove any prior reaction from this user for this emoji (re-send case)
      const filtered = emojiList.filter((r) => r.userId !== userId);

      return {
        reactions: {
          ...state.reactions,
          [roomId]: {
            ...roomReactions,
            [targetEventId]: {
              ...msgReactions,
              [emoji]: [...filtered, { userId, reactionEventId }]
            }
          }
        }
      };
    });
  },

  removeReactionByEventId: (roomId, reactionEventId) => {
    set((state) => {
      const roomReactions = state.reactions[roomId];
      if (!roomReactions) return state;

      const nextRoom = {};
      let changed = false;

      for (const [targetEventId, emojiMap] of Object.entries(roomReactions)) {
        const nextEmoji = {};
        for (const [emoji, list] of Object.entries(emojiMap)) {
          const filtered = list.filter((r) => r.reactionEventId !== reactionEventId);
          if (filtered.length !== list.length) changed = true;
          if (filtered.length > 0) nextEmoji[emoji] = filtered;
        }
        if (Object.keys(nextEmoji).length > 0) nextRoom[targetEventId] = nextEmoji;
      }

      if (!changed) return state;
      return {
        reactions: {
          ...state.reactions,
          [roomId]: nextRoom
        }
      };
    });
  },

  // ===== MESSAGE EDIT / DELETE =====
  updateMessageBody: (roomId, eventId, newBody) => {
    set((state) => {
      const current = state.messages[roomId];
      if (!current) return state;

      const index = current.findIndex((m) => m.eventId === eventId);
      if (index === -1) return state;

      const next = [...current];
      next[index] = { ...next[index], body: newBody, edited: true };

      return {
        messages: {
          ...state.messages,
          [roomId]: next,
        },
      };
    });
  },

  redactMessage: (roomId, eventId) => {
    set((state) => {
      const current = state.messages[roomId];
      if (!current) return state;

      const index = current.findIndex((m) => m.eventId === eventId);
      if (index === -1) return state;

      const next = [...current];
      next[index] = { ...next[index], body: '', deleted: true };

      return {
        messages: {
          ...state.messages,
          [roomId]: next,
        },
      };
    });
  },

  // ===== USER PRESENCE =====
  presence: {}, // { [userId]: 'online' | 'offline' | 'unavailable' }

  setUserPresence: (userId, presence) => {
    set((state) => ({
      presence: {
        ...state.presence,
        [userId]: presence
      }
    }));
  },

    // ===== UNREAD COUNTS =====
  unreadCounts: {}, // { [roomId]: number }

  setUnreadCount: (roomId, count) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [roomId]: Math.max(0, count)
      }
    }));
  },

  incrementUnreadCount: (roomId) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [roomId]: (state.unreadCounts[roomId] || 0) + 1
      }
    }));
  },


  clearUnreadCount: (roomId) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [roomId]: 0
      }
    }));
  },

  // ===== DURABLE READ STATE (Strategy A) =====
  // Hydrated for the current mxid; used by ChatWorld to decide whether to show "NEW"
  readStateMxid: null,
  lastReadTsByRoom: {}, // { [roomId]: number (ms) }

  hydrateReadStateForMxid: (mxid) => {
    const safeMxid = String(mxid || '').trim();
    const map = safeMxid ? loadReadStateMap(safeMxid) : {};
    set({
      readStateMxid: safeMxid || null,
      lastReadTsByRoom: map
    });
  },

  markRoomRead: (mxid, roomId, ts) => {
    const safeMxid = String(mxid || '').trim();
    const safeRoomId = String(roomId || '').trim();
    const safeTs = Number(ts) || 0;

    if (!safeMxid || !safeRoomId || !safeTs) return;

    set((state) => {
      // If mxid changed without hydration, avoid polluting the wrong user’s map.
      const base =
        state.readStateMxid === safeMxid && state.lastReadTsByRoom
          ? state.lastReadTsByRoom
          : loadReadStateMap(safeMxid);

      const next = { ...(base || {}), [safeRoomId]: safeTs };

      saveReadStateMap(safeMxid, next);

      return {
        readStateMxid: safeMxid,
        lastReadTsByRoom: next
      };
    });
  },

// ===== MESSAGES DATA =====
messages: {}, // { [roomId]: message[] }


// Messages management
addMessage: (roomId, message) => {
  set((state) => {
    const current = state.messages[roomId] || [];

    // ensure an id so React has a stable key
    const enriched = { ...message };
    if (!enriched.id) {
      enriched.id = `sys-${Date.now()}-${Math.random()}`;
    }

    return {
      messages: {
        ...state.messages,
        [roomId]: [...current, enriched],
      },
    };
  });
},
addMatrixEvent: (roomId, eventObj) => {
  set((state) => {
    const current = state.messages[roomId] || [];

    const enriched = {
      ...eventObj,
    };
    if (!enriched.id) {
      enriched.id =
        eventObj.eventId ||
        eventObj.txnId ||
        `msg-${Date.now()}-${Math.random()}`;
    }

    const { eventId, txnId, sender, body } = enriched;

    // 1) Primary match: same eventId or txnId (Matrix echo / promotion)
    let index = -1;
    if (eventId) {
      index = current.findIndex((m) => m.eventId && m.eventId === eventId);
    }
    if (index === -1 && txnId) {
      index = current.findIndex((m) => m.txnId && m.txnId === txnId);
    }

    // 2) Fallback match: same sender + same body (treat as the same message)
    if (index === -1 && sender && body) {
      index = current.findIndex(
        (m) => m.sender === sender && m.body === body
      );
    }

    let next;
    if (index >= 0) {
      next = [...current];
      next[index] = { ...next[index], ...enriched };
    } else {
      next = [...current, enriched];
    }

    next.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

    return {
      messages: {
        ...state.messages,
        [roomId]: next,
      },
    };
  });
},



  setMessages: (roomId, messages) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: messages.sort((a, b) => a.timestamp - b.timestamp)
      }
    }));
  },
  // ===== USER PROFILES CACHE =====

  userProfiles: {}, // { [u_id]: { name: string, avatarUrl: string } }

  setUserProfile: (u_id, profile) => {
    set((state) => ({
      userProfiles: {
        ...state.userProfiles,
        [u_id]: {
          ...state.userProfiles[u_id],
          ...profile,
          lastUpdated: Date.now()
        }
      }
    }));
  },

  // ===== ACCEPTANCE MARKERS (idempotent per room) =====
  // Tracks which rooms have already processed the "invite accepted" handoff
  // so we do not keep adding duplicate "Connection successful" messages.
  acceptanceMarkers: {}, // { [roomId: string]: number /* acceptedAt */ },

// ===== MATRIX SELECTORS & HELPERS =====
// Get all rooms as array, sorted by last activity
getSortedRooms: () => {
  const { rooms } = get();
  return Object.values(rooms).sort((a, b) => {
    const aTime = a.lastMessageTs || a.lastUpdated || 0;
    const bTime = b.lastMessageTs || b.lastUpdated || 0;
    return bTime - aTime;
  });
},

// Get messages for a room
getRoomMessages: (roomId) => {
  return get().messages[roomId] || [];
},

// Get unread count for a room
getRoomUnreadCount: (roomId) => {
  return get().unreadCounts[roomId] || 0;
},

// Get total unread count across all rooms
getTotalUnreadCount: () => {
  const unreadCounts = get().unreadCounts;
  return Object.values(unreadCounts).reduce((total, count) => total + count, 0);
},

// Get typing users for a room
getRoomTypingUsers: (roomId) => {
  return get().typingUsers[roomId] || [];
},

// Get user profile
getUserProfile: (u_id) => {
  return get().userProfiles[u_id] || null;
},

// Get user presence
getUserPresence: (userId) => {
  return get().presence[userId] || 'offline';
},

// Get room by ID
getRoom: (roomId) => {
  return get().rooms[roomId] || null;
},

// Get room by peer user ID
getRoomByPeerId: (peerUid) => {
  const rooms = get().rooms;
  return Object.values(rooms).find(room => room.peerUserId === peerUid) || null;
},

// Check if user is typing in a room
isUserTyping: (roomId, userId) => {
  const typingUsers = get().typingUsers[roomId] || [];
  return typingUsers.includes(userId);
},

// Primary selection API
setSelectedRoomId: (roomId) => {
  set({ selectedRoomId: roomId || null });
},


  // ===== ACCEPTED DM HANDOFF (from Matrix state event) =====
  handleInviteAccepted: (roomId, content) => {
    const { inviter, invitee, acceptedAt } = content || {};

    // Defensive: if we didn't get a roomId, do nothing.
    if (!roomId) {
      console.warn('[MatrixStore] handleInviteAccepted called without roomId', {
        content,
      });
      return;
    }

    const roomKey = String(roomId);
    const markers = get().acceptanceMarkers || {};
    const alreadyProcessed = !!markers[roomKey];

    console.log('[MatrixStore] handleInviteAccepted', {
      roomId: roomKey,
      inviter,
      invitee,
      acceptedAt,
      alreadyProcessed,
    });

    // Figure out who "I" am, and who the peer is, based on the inviter/invitee IDs
    let myUid = null;
    try {
      const raw = window.localStorage.getItem('u_id');
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) myUid = n;
    } catch {
      // ignore
    }

    // If we cannot determine the current app user, still focus the room,
    // but skip optimistic-thread cleanup to avoid deleting wrong data.
    let peerUid = null;
    if (
      myUid &&
      (Number.isFinite(Number(inviter)) || Number.isFinite(Number(invitee)))
    ) {
      const inv = Number(inviter);
      const invt = Number(invitee);

      if (myUid === inv && Number.isFinite(invt)) {
        peerUid = invt;
      } else if (myUid === invt && Number.isFinite(inv)) {
        peerUid = inv;
      }
    }

    // 1) Focus the real Matrix room inside matrixStore and mark acceptance once
    set((state) => ({
      selectedRoomId: roomId,
      acceptanceMarkers: {
        ...(state.acceptanceMarkers || {}),
        [roomKey]: acceptedAt || Date.now(),
      },
    }));

    // 2) Coordinate with messagingStore: remove the optimistic thread and pending stub,
    //    and move the ChatView selection to the real Matrix roomId.
    try {
      const ms = useMessagingStore.getState();
      const currentOptimistic = ms.optimisticThreads || [];
      const currentPending = ms.pendingThreads || {};

      let nextOptimistic = currentOptimistic;
      let nextPending = currentPending;

      if (peerUid) {
        const peerStr = String(peerUid);

        // Drop any optimistic thread that matches this peer (or any alias that we used for this pair)
        nextOptimistic = currentOptimistic.filter((t) => {
          const alias = String(t.alias || '');
          const peer = String(t.peerUid ?? '');
          if (peer && peer === peerStr) return false;
          if (alias && alias === roomKey) return false;
          return true;
        });

        // Drop any legacy pendingThread entry for this peer
        const pendingCopy = { ...(currentPending || {}) };
        const key = Number(peerUid);
        if (Number.isFinite(key) && pendingCopy[key]) {
          delete pendingCopy[key];
        }
        nextPending = pendingCopy;
      }

      useMessagingStore.setState({
        optimisticThreads: nextOptimistic,
        pendingThreads: nextPending,
        selectedThread: roomId,
      });
    } catch (err) {
      console.error(
        '[MatrixStore] handleInviteAccepted → messagingStore failed',
        err
      );
    }

    // 3) Only drop the system message the FIRST time for this room.
    if (alreadyProcessed) {
      console.log(
        '[MatrixStore] handleInviteAccepted: system message already added for room, skipping'
      );
      return;
    }

    const timestamp = acceptedAt || Date.now();
    get().addMessage(roomId, {
      body: 'Connection successful - get to chatting!!',
      system: true,
      timestamp,
      isMine: false,
    });
  },




// Reset entire store (for logout)
  reset: () => {
    set({
      connectionState: 'disconnected',
      rooms: {},
      messages: {},
      unreadCounts: {},
      typingUsers: {},
      presence: {},
      remoteReadReceipts: {},
      reactions: {},
      userProfiles: {},
      acceptanceMarkers: {},
      paginationState: {},
      isOpen: false,
      candidateStarter: null,
      selectedRoomId: null,

      // Read-state stays on disk; only clear in-memory copy on logout.
      readStateMxid: null,
      lastReadTsByRoom: {}
    });
  },




  // ===== UI ACTIONS =====
  // Flag to skip auto-selection when opening from docking station with no specific user
  skipAutoSelect: false,
  setSkipAutoSelect: (val) => set({ skipAutoSelect: !!val }),

  open: () => set({ isOpen: true }),
  
  close: () => set((state) => ({
    isOpen: false,
    candidateStarter: null,
    skipAutoSelect: false,
    selectedThread: state.selectedThread
  })),

  // ===== CREATE DM PROCESS (FULLY WORKING) =====
openWithCandidate: (payload) => {
    // Strong validation + logging
    if (!payload || typeof payload !== 'object') {
      console.error('[MatrixStore][openWithCandidate] Invalid payload (not an object):', payload);
      return;
    }

    console.log('[MatrixStore][openWithCandidate] Called with payload:', payload);

    const myUserIdRaw = window.localStorage.getItem('u_id');
    const myUserId = Number(myUserIdRaw);
    const candidateIdRaw =
      payload.candidateId ??
      payload.candidate_id ??
      payload.u_id ??
      payload.userId;

    const candidateUserId = Number(candidateIdRaw);

    if (!Number.isFinite(myUserId) || !Number.isFinite(candidateUserId)) {
      console.error('[MatrixStore][openWithCandidate] Invalid user IDs for DM creation', {
        myUserIdRaw,
        myUserId,
        candidateIdRaw,
        candidateUserId,
        payload,
      });
      return;
    }

    // Compute canonical alias (#eh.dm.u<min>_u<max>:ehandshake.com) for stable identity
    const a = Math.min(myUserId, candidateUserId);
    const b = Math.max(myUserId, candidateUserId);
    const aliasFull = `#eh.dm.u${a}_u${b}:ehandshake.com`;

    // 1) Open ChatCenter and select the new thread immediately
    set({ isOpen: true, skipAutoSelect: false });
    useMessagingStore.setState({ selectedThread: aliasFull });

    try {
      // legacy pending map (keyed by peer uid)
      useMessagingStore.getState().addPendingThread(candidateUserId, {
        name: payload.candidateName || payload.name || null,
        avatarUrl: payload.candidateAvatar || payload.avatarUrl || null,
        createdAt: Date.now(),
      });
    } catch (err) {
      console.error('[MatrixStore][openWithCandidate] addPendingThread failed', err);
    }

    try {
      // 2) Optimistic thread with direction so ChatWorld shows the invite compose UI
      useMessagingStore.getState().addOptimisticDM({
        alias: aliasFull,
        peerUid: candidateUserId,
        displayName: payload.candidateName || payload.name || null,
        avatarUrl: payload.candidateAvatar || payload.avatarUrl || null,
        lastMessagePreview: 'Start a conversation…',
        lastMessageAt: Date.now(),
        direction: 'outgoing',
        inviteSent: false,
      });
    } catch (err) {
      console.error('[MatrixStore][openWithCandidate] addOptimisticDM failed', err);
    }
  },


  clearCandidateStarter: () => set({ candidateStarter: null }),

}));

// ===== CONVENIENCE HOOKS =====
export const useMatrixConnection = () => useMatrixStore(state => state.connectionState);
export const useIsMessagingOpen = () => useMatrixStore(state => state.isOpen);
export const useMatrixRooms = () => useMatrixStore(state => state.getSortedRooms());
export const useTotalUnreadCount = () => useMatrixStore(state => state.getTotalUnreadCount());
export const useRoomMessages = (roomId) => useMatrixStore(state => state.getRoomMessages(roomId));
export const useRoomTyping = (roomId) => useMatrixStore(state => state.getRoomTypingUsers(roomId));
export const useSelectedRoomId = () => useMatrixStore((s) => s.selectedRoomId);